// Deep-link pre-fill for the questionnaire.
//
// A marketing page that already knows part of the answer links in with a
// `?prefill=<area>-<care>[-<intent>]` token so the visitor doesn't re-tap questions
// they've effectively already answered. Consumers:
//   - the /surgery/ page: `knee-surgery` seeds body area = Knee, care type = SURGERY
//     and jumps straight to which procedure;
//   - the /learn/ hub (expy-learn): its knee check sends e.g. `knee-injury-self` or
//     `knee-chronic-pt`, adding how they want to do it (the intent question);
//   - the /embed/picker frame on WordPress pages: a bare `knee` is a tap on the picker.
//     It travels in the URL because storage written inside the frame is not reliably
//     visible to the top-level funnel on mobile browsers.
// Seeded fields are skipped on the way forward (see Questionnaire) but stay
// reachable with Back.
//
// Phase (preparing vs recovering) is deliberately NOT seeded: the funnel derives it
// from the surgery date the visitor still enters (see derivePlanPhase on the API),
// so the date question does that work and one token serves both phases.
//
// Rules, all fail-safe:
//   - a full token only ever seeds a FRESH session (no body part chosen yet), so it
//     can never clobber a funnel already in progress; a bare area is an explicit new
//     choice, so a different area starts a new plan (see seedBodyArea);
//   - the token is removed from the address bar once read;
//   - every value is validated against the real option lists — an unknown area or
//     care keyword is ignored;
//   - any missing/malformed param or storage error is a silent no-op and the
//     funnel simply starts at question one.
//
// The attribution layer separately records the `ref` tag, so an abandoned
// pre-filled session is still traceable even though the seeded answers only reach
// the server at completion.

import { BODY_AREAS, visibleSteps } from './flow';
import { loadAnswers, saveAnswers, saveSeeded, saveStep, type Answers } from './session';

// Token keywords → option values. Anything else is ignored (fail-safe).
export const CARE_KEYWORDS: Record<string, string> = {
  surgery: 'SURGERY',
  injury: 'INJURY',
  chronic: 'CHRONIC',
  unsure: 'UNSURE',
};
export const INTENT_KEYWORDS: Record<string, string> = {
  self: 'SELF',
  pt: 'WANT_PT',
  unsure: 'UNSURE',
};

function matchBodyArea(raw: string): string | null {
  const norm = raw.trim().toLowerCase();
  return BODY_AREAS.find((b) => b.toLowerCase() === norm) ?? null;
}

/**
 * Seed question one from an explicit tap on a body-area picker (the landing page, or the
 * /embed/picker frame on WordPress pages via `?prefill=<area>`). Unlike a full token this
 * is a fresh choice, so it may replace a funnel in progress: tapping the area already in
 * progress resumes it, a different area starts a new plan.
 */
export function seedBodyArea(area: string): void {
  if (loadAnswers().bodyPart === area) return;
  const seeded: Answers = { bodyPart: area };
  saveAnswers(seeded);
  const firstOpen = visibleSteps(seeded).findIndex((q) => seeded[q.field] === undefined);
  saveStep(firstOpen < 0 ? 0 : firstOpen);
}

// The token is health context: once read, take it out of the address bar and history.
function dropPrefillFromUrl(): void {
  try {
    const u = new URL(window.location.href);
    u.searchParams.delete('prefill');
    window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
  } catch {
    /* keep the URL as is */
  }
}

/** Parse a prefill token into the answers it seeds, or null if it isn't valid. */
export function parsePrefill(token: string): Answers | null {
  const parts = token.trim().toLowerCase().split('-');
  if (parts.length < 2 || parts.length > 3) return null;
  const bodyPart = matchBodyArea(parts[0]);
  const careType = CARE_KEYWORDS[parts[1]];
  if (!bodyPart || !careType) return null;
  const seeded: Answers = { bodyPart, careType };
  // An unknown intent keyword drops only the intent, never the whole token.
  const intent = parts[2] ? INTENT_KEYWORDS[parts[2]] : undefined;
  if (intent) seeded.intent = intent;
  return seeded;
}

/**
 * Read `?prefill=<area>` (a picker tap, e.g. "knee") or `?prefill=<area>-<care>[-<intent>]`
 * (e.g. "knee-surgery", "knee-injury-self"),
 * seed the matching answers, and set the starting step to the first unanswered
 * question. Call once at boot, before first render, on the funnel route only.
 */
export function applyPrefillFromUrl(): void {
  let token: string;
  try {
    token = (new URLSearchParams(window.location.search).get('prefill') || '').trim();
  } catch {
    return;
  }
  if (!token) return;
  dropPrefillFromUrl();

  // `?prefill=<area>` alone = a tap on a body-area picker.
  const areaOnly = !token.includes('-') ? matchBodyArea(token) : null;
  if (areaOnly) {
    seedBodyArea(areaOnly);
    return;
  }

  const fromToken = parsePrefill(token);
  if (!fromToken) return;

  // Never overwrite a funnel already in progress — a returning visitor resumes.
  const existing = loadAnswers();
  if (existing.bodyPart) return;

  const seeded: Answers = { ...existing, ...fromToken };
  saveAnswers(seeded);
  saveSeeded(Object.keys(fromToken));

  // Land on the first question we still need an answer for (e.g. which surgery).
  const steps = visibleSteps(seeded);
  const firstUnanswered = steps.findIndex((q) => seeded[q.field] === undefined);
  saveStep(firstUnanswered < 0 ? 0 : firstUnanswered);
}
